/**
 * Admin API client mode switch.
 *
 * Pick the underlying client (mock or real) based on `VITE_ADMIN_API_MODE`
 * and re-export every read function with a stable signature so the
 * contract barrel (admin-contract.ts) doesn't need conditional imports.
 *
 * Feature pages keep importing from `@/lib/api/admin-contract` and never
 * know whether they're talking to mocks or HTTP.
 *
 * Mutations are intentionally NOT re-exported here. They'll land later
 * behind a `@dangerous_action` flow.
 */

import { env, type AdminApiMode } from "@/lib/env/env";

import * as mockClient from "@/lib/api/admin-mock-client";
import * as realClient from "@/lib/api/admin-real-client";

export const ADMIN_API_MODE: AdminApiMode = env.VITE_ADMIN_API_MODE;
export const isAdminMockMode = ADMIN_API_MODE === "mock";
export const isAdminRealMode = ADMIN_API_MODE === "real";

/**
 * Shape every admin read client must satisfy. Listed explicitly so a
 * drift between mock and real surfaces at compile time.
 */
export interface AdminReadClient {
  getAdminOperationsSummary: typeof mockClient.getAdminOperationsSummary;
  getAdminMembershipFunnel: typeof mockClient.getAdminMembershipFunnel;
  getAdminNavigationMetrics: typeof mockClient.getAdminNavigationMetrics;
  searchAdminUsers: typeof mockClient.searchAdminUsers;
  getAdminUserDetail: typeof mockClient.getAdminUserDetail;
  getAdminUserTimeline: typeof mockClient.getAdminUserTimeline;
  getAdminUserAuditEvents: typeof mockClient.getAdminUserAuditEvents;
  getAdminUserSessions: typeof mockClient.getAdminUserSessions;
  getAdminUserMembership: typeof mockClient.getAdminUserMembership;
  getAdminUserGifts: typeof mockClient.getAdminUserGifts;
  getAdminGiftObligations: typeof mockClient.getAdminGiftObligations;
  getAdminGiftRiskQueue: typeof mockClient.getAdminGiftRiskQueue;
  getAdminUserHardware: typeof mockClient.getAdminUserHardware;
  getAdminUserReclaim: typeof mockClient.getAdminUserReclaim;
  getAdminUserLostReports: typeof mockClient.getAdminUserLostReports;
  getAdminReplacementPrice: typeof mockClient.getAdminReplacementPrice;
  getAdminAppVersionFloors: typeof mockClient.getAdminAppVersionFloors;
  getAdminPolicyVersions: typeof mockClient.getAdminPolicyVersions;
  listAdminMemberships: typeof mockClient.listAdminMemberships;
  listAdminHardware: typeof mockClient.listAdminHardware;
  listAdminReclaims: typeof mockClient.listAdminReclaims;
  getAdminUserRiskSignals: typeof mockClient.getAdminUserRiskSignals;
  listAdminDisputes: typeof mockClient.listAdminDisputes;
  getAdminDisputeDetail: typeof mockClient.getAdminDisputeDetail;
  getAdminDisputeEvidence: typeof mockClient.getAdminDisputeEvidence;
  listAdminAuditLogs: typeof mockClient.listAdminAuditLogs;
  getAdminAuditEvent: typeof mockClient.getAdminAuditEvent;
  listAdminSupportActionPlaceholders: typeof mockClient.listAdminSupportActionPlaceholders;
  listAdminSupportActionQueue: typeof mockClient.listAdminSupportActionQueue;
  listAdminShipments: typeof mockClient.listAdminShipments;
  getAdminUserBillingRetry: typeof mockClient.getAdminUserBillingRetry;
  getAdminUserPayments: typeof mockClient.getAdminUserPayments;
  getAdminMembershipNoticeSummary: typeof mockClient.getAdminMembershipNoticeSummary;
  listAdminMembershipNoticesFailed: typeof mockClient.listAdminMembershipNoticesFailed;
  listAdminMembershipNoticesDuePending: typeof mockClient.listAdminMembershipNoticesDuePending;
  getAdminOutboxHealth: typeof mockClient.getAdminOutboxHealth;
  getAdminEasypostWebhookHealth: typeof mockClient.getAdminEasypostWebhookHealth;
  listAdminEasypostWebhookEvents: typeof mockClient.listAdminEasypostWebhookEvents;
}

const _mock: AdminReadClient = mockClient;
const _real: AdminReadClient = realClient;

export const adminReadClient: AdminReadClient = isAdminRealMode ? _real : _mock;

/**
 * Stable re-exports — these are what `admin-contract.ts` re-publishes.
 * They are bound to whichever underlying client matches the env mode at
 * module-load time. Hot-reload boundaries are unchanged from before.
 */
export const getAdminOperationsSummary =
  adminReadClient.getAdminOperationsSummary;
export const getAdminMembershipFunnel =
  adminReadClient.getAdminMembershipFunnel;
export const getAdminNavigationMetrics =
  adminReadClient.getAdminNavigationMetrics;
export const searchAdminUsers = adminReadClient.searchAdminUsers;
export const getAdminUserDetail = adminReadClient.getAdminUserDetail;
export const getAdminUserTimeline = adminReadClient.getAdminUserTimeline;
export const getAdminUserAuditEvents = adminReadClient.getAdminUserAuditEvents;
export const getAdminUserSessions = adminReadClient.getAdminUserSessions;
export const getAdminUserMembership = adminReadClient.getAdminUserMembership;
export const getAdminUserGifts = adminReadClient.getAdminUserGifts;
export const getAdminGiftObligations =
  adminReadClient.getAdminGiftObligations;
export const getAdminGiftRiskQueue = adminReadClient.getAdminGiftRiskQueue;
export const getAdminUserHardware = adminReadClient.getAdminUserHardware;
export const getAdminUserReclaim = adminReadClient.getAdminUserReclaim;
export const getAdminUserLostReports =
  adminReadClient.getAdminUserLostReports;
export const getAdminReplacementPrice =
  adminReadClient.getAdminReplacementPrice;
export const getAdminAppVersionFloors =
  adminReadClient.getAdminAppVersionFloors;
export const getAdminPolicyVersions = adminReadClient.getAdminPolicyVersions;
export const listAdminMemberships = adminReadClient.listAdminMemberships;
export const listAdminHardware = adminReadClient.listAdminHardware;
export const listAdminReclaims = adminReadClient.listAdminReclaims;
export const getAdminUserRiskSignals = adminReadClient.getAdminUserRiskSignals;
export const listAdminDisputes = adminReadClient.listAdminDisputes;
export const getAdminDisputeDetail = adminReadClient.getAdminDisputeDetail;
export const getAdminDisputeEvidence = adminReadClient.getAdminDisputeEvidence;
export const listAdminAuditLogs = adminReadClient.listAdminAuditLogs;
export const getAdminAuditEvent = adminReadClient.getAdminAuditEvent;
export const listAdminSupportActionPlaceholders =
  adminReadClient.listAdminSupportActionPlaceholders;
export const listAdminSupportActionQueue =
  adminReadClient.listAdminSupportActionQueue;
export const listAdminShipments = adminReadClient.listAdminShipments;
export const getAdminUserBillingRetry =
  adminReadClient.getAdminUserBillingRetry;
export const getAdminUserPayments = adminReadClient.getAdminUserPayments;
export const getAdminMembershipNoticeSummary =
  adminReadClient.getAdminMembershipNoticeSummary;
export const listAdminMembershipNoticesFailed =
  adminReadClient.listAdminMembershipNoticesFailed;
export const listAdminMembershipNoticesDuePending =
  adminReadClient.listAdminMembershipNoticesDuePending;
export const getAdminOutboxHealth = adminReadClient.getAdminOutboxHealth;
export const getAdminEasypostWebhookHealth =
  adminReadClient.getAdminEasypostWebhookHealth;
export const listAdminEasypostWebhookEvents =
  adminReadClient.listAdminEasypostWebhookEvents;
